/**
 * The 62 chips of Avalon.
 *
 * Transcribed from the official Roxley rulebook (2025.06.10). The `text` field
 * is the rulebook's own wording, near-verbatim — it is what the player reads,
 * and it is the contract the engine in `engine.ts` implements. Where the
 * rulebook's glossary overrules the chip's abbreviated text, the glossary wins
 * and the difference is noted in a comment on the ability.
 *
 * Counts sum to exactly 62. That total is asserted at module load: a catalogue
 * that silently drifts to 61 changes every probability in the game and would
 * otherwise surface as "the deck feels off" three weeks later.
 */

export type ChipId =
  | 'arthur'
  | 'artisan'
  | 'baroness'
  | 'blackKnight'
  | 'caelia'
  | 'cursedBlade'
  | 'excalibur'
  | 'fisherKing'
  | 'galahad'
  | 'gawain'
  | 'greenKnight'
  | 'guinevere'
  | 'ladyOfTheLake'
  | 'lancelot'
  | 'merchant'
  | 'merlin'
  | 'mordred'
  | 'morgana'
  | 'peasant'
  | 'percival'
  | 'priest'
  | 'robyn'
  | 'spirit'
  | 'spy'
  | 'squireSword'
  | 'thief'
  | 'uther';

/**
 * The artwork's gender coding, which exists for exactly one reason: Caelia
 * trashes chips "featuring male artwork (denoted with blue borders)". The three
 * swords are neither — they carry silver borders, and they are un-trashable
 * anyway, so Caelia can never remove one.
 */
export type Gender = 'male' | 'female' | 'sword';

export interface Chip {
  id: ChipId;
  name: string;
  /** How many exist in the box. The catalogue is built from these. */
  count: number;
  gender: Gender;
  /** Rulebook text, shown on the chip. */
  text: string;
  /** One-line reminder used in tight UI (the played-chip strip). */
  brief: string;
  /**
   * Name for the 56px in-hand chip, where the full one would ellipsise.
   * "Cursed B..." on the chip that decides who loses is not a small problem.
   */
  short?: string;
  /**
   * Fires at Reveal, before any normal resolution. Multiple On Reveal effects
   * resolve in turn order.
   */
  onReveal?: boolean;
  /** Stays in play by its owner as a faceup chip, granting its ability. */
  permanent?: boolean;
}

export const CHIPS: Record<ChipId, Chip> = {
  arthur: {
    id: 'arthur',
    name: 'Arthur',
    count: 1,
    gender: 'male',
    text: 'Anyone with swords holds them out facedown. Without peeking, steal 1 and give back the rest.',
    brief: 'Blindly steal 1 sword from the table.',
  },
  artisan: {
    id: 'artisan',
    name: 'Artisan',
    count: 5,
    gender: 'male',
    text: 'Draw 2 chips or steal 2 chips, then place 2 from your hand into any regions in Avalon (facedown).',
    brief: 'Draw 2 or steal 2, then stash 2 in Avalon.',
  },
  baroness: {
    id: 'baroness',
    name: 'Baroness',
    count: 4,
    gender: 'female',
    text: 'Draw 2 chips, then trash 1 chip from your hand to the box (faceup). You cannot trash a sword.',
    brief: 'Draw 2, then trash 1 (never a sword).',
  },
  blackKnight: {
    id: 'blackKnight',
    name: 'Black Knight',
    count: 1,
    gender: 'male',
    text: 'On reveal, flip this chip twice (like a coin toss). If either flip lands faceup, take 1 played chip from any player to your hand. They lose its ability.',
    brief: 'Flip twice; on a win, take a played chip.',
    short: 'Black Kt',
    onReveal: true,
  },
  caelia: {
    id: 'caelia',
    name: 'Caelia',
    count: 1,
    gender: 'female',
    text: 'Point to up to two players. They must trash any chips in their hands featuring male artwork.',
    brief: 'Two players trash every male chip they hold.',
  },
  cursedBlade: {
    id: 'cursedBlade',
    name: 'Cursed Blade',
    count: 1,
    gender: 'sword',
    text: "This sword can't be played or trashed (but it can change hands). If you have this chip at game's end you can't win, and you are instead the ultimate loser.",
    brief: 'Hold it at the end and you are the ultimate loser.',
    short: 'Cursed',
  },
  excalibur: {
    id: 'excalibur',
    name: 'Excalibur',
    count: 1,
    gender: 'sword',
    text: "This sword can't be played or trashed (but it can change hands). If you have this chip at game's end, you win!",
    brief: "Hold it at game's end and you win.",
  },
  fisherKing: {
    id: 'fisherKing',
    name: 'Fisher King',
    count: 1,
    gender: 'male',
    text: 'Permanent: You may flip Fisher King anytime. On faceup, effects saying "steal" take 0 chips from you this round. On facedown, trash Fisher King (no effect).',
    brief: 'Permanent. Flip to shrug off steals this round.',
    short: 'Fisher',
    permanent: true,
  },
  galahad: {
    id: 'galahad',
    name: 'Galahad',
    count: 1,
    gender: 'male',
    text: 'On Reveal, close your fist on 1 chip from your hand. This chip is protected from all effects this round.',
    brief: 'Protect 1 chip in your hand for the round.',
    onReveal: true,
  },
  gawain: {
    id: 'gawain',
    name: 'Gawain',
    count: 1,
    gender: 'male',
    text: 'Pick a player. If they have a sword, they must give one of their swords to you (their choice).',
    brief: 'One player hands you a sword of their choosing.',
  },
  greenKnight: {
    id: 'greenKnight',
    name: 'Green Knight',
    count: 1,
    gender: 'male',
    text: 'Anyone with swords must give them to you.',
    brief: 'Every sword on the table comes to you.',
    short: 'Green Kt',
  },
  guinevere: {
    id: 'guinevere',
    name: 'Guinevere',
    count: 1,
    gender: 'female',
    text: 'Anyone with swords holds them out facedown. Without peeking, place up to 2 in 1 region & mix it.',
    brief: 'Blindly bury up to 2 swords in one region.',
  },
  ladyOfTheLake: {
    id: 'ladyOfTheLake',
    name: 'Lady of the Lake',
    count: 1,
    gender: 'female',
    text: 'Point left or right. All players simultaneously pass their hand in that direction.',
    brief: 'Everyone passes their whole hand one way.',
    short: 'Lady',
  },
  lancelot: {
    id: 'lancelot',
    name: 'Lancelot',
    count: 1,
    gender: 'male',
    text: 'On reveal, all players must flip their played chips (like a coin toss). If the flip lands facedown, they trash their chip and lose its ability.',
    brief: 'Every played chip flips; failures are trashed.',
    onReveal: true,
  },
  merchant: {
    id: 'merchant',
    name: 'Merchant',
    count: 7,
    gender: 'male',
    text: 'Draw 2 chips or steal 2 chips, then return 1 chip.',
    brief: 'Draw 2 or steal 2, then give 1 back.',
  },
  merlin: {
    id: 'merlin',
    name: 'Merlin',
    count: 1,
    gender: 'male',
    text: 'Anyone with swords must give them to you. Then in turn order, those who gave up one or more swords must steal 1 chip from your hand.',
    brief: 'Take every sword; the robbed each steal 1 back.',
  },
  mordred: {
    id: 'mordred',
    name: 'Mordred',
    count: 1,
    gender: 'male',
    text: "Peek at a player's hand. You may choose to swap hands with that player.",
    brief: 'Peek at a hand, then optionally take it.',
  },
  morgana: {
    id: 'morgana',
    name: 'Morgana',
    count: 1,
    gender: 'female',
    text: 'Draw 2 chips, then you may immediately reveal & resolve 1 of those two chips (trashing it as normal).',
    brief: 'Draw 2, then fire one of them off at once.',
  },
  peasant: {
    id: 'peasant',
    name: 'Peasant',
    count: 8,
    gender: 'female',
    text: 'Draw 1 chip or steal 1 chip, then return 1 chip.',
    brief: 'Draw 1 or steal 1, then give 1 back.',
  },
  percival: {
    id: 'percival',
    name: 'Percival',
    count: 1,
    gender: 'male',
    text: 'Turn over 4 chips in Avalon faceup for all to see. Take 1 of them, then turn the other 3 facedown.',
    brief: 'Expose 4 chips in Avalon and take your pick.',
  },
  priest: {
    id: 'priest',
    name: 'Priest',
    count: 5,
    gender: 'male',
    text: 'Draw 2 chips. If you drew a sword, you must shout: "Huzzah!"',
    brief: 'Draw 2 — and announce it if a sword comes up.',
  },
  robyn: {
    id: 'robyn',
    name: 'Robyn of Roxley',
    count: 1,
    gender: 'female',
    text: 'Steal 1 chip. You must repeat this (if able) until you have 7 chips in hand. You may choose a new target for each steal.',
    brief: 'Steal until your hand reaches seven.',
    short: 'Robyn',
  },
  spirit: {
    id: 'spirit',
    name: 'Spirit',
    count: 5,
    gender: 'female',
    text: 'Point left or right. All players lay down their hands, then simultaneously steal 1 chip from the hand of the player in the chosen direction.',
    brief: 'Everyone steals 1 from their neighbour, one way.',
  },
  spy: {
    id: 'spy',
    name: 'Spy',
    count: 3,
    gender: 'female',
    text: "Peek at a player's hand. Show 1 chip from their hand to all players, then give their hand back.",
    brief: 'Peek at a hand and expose 1 chip to the table.',
  },
  squireSword: {
    id: 'squireSword',
    name: 'Squire Sword',
    count: 1,
    gender: 'sword',
    text: "This sword can't be played or trashed (but it can change hands). You also win if you hold the Squire Sword and the player on your left wins, except in 2 and 3 player games.",
    brief: 'You also win if the player on your left wins.',
    short: 'Squire',
  },
  thief: {
    id: 'thief',
    name: 'Thief',
    count: 6,
    gender: 'male',
    text: 'Steal 1 chip. Then steal 1 chip from a different player (if able).',
    brief: 'Steal 1, then 1 more from someone else.',
  },
  uther: {
    id: 'uther',
    name: 'Uther',
    count: 1,
    gender: 'male',
    text: 'Point left or right. Anyone with swords must pass them in that direction.',
    brief: 'Every sword slides one seat, your way.',
  },
};

/** The three chips the glossary means by the word "Sword". */
export const SWORDS: readonly ChipId[] = ['excalibur', 'cursedBlade', 'squireSword'] as const;

export function isSword(id: ChipId): boolean {
  return CHIPS[id].gender === 'sword';
}

/** Swords can never be chosen at the Play step. */
export function isPlayable(id: ChipId): boolean {
  return !isSword(id);
}

/** Swords can never be trashed — by the Baroness, by Caelia, or by Lancelot. */
export function isTrashable(id: ChipId): boolean {
  return !isSword(id);
}

export const ALL_CHIP_IDS = Object.keys(CHIPS) as ChipId[];

/** Every physical chip in the box, one entry per copy. 62 long. */
export function fullBox(): ChipId[] {
  const out: ChipId[] = [];
  for (const id of ALL_CHIP_IDS) {
    for (let i = 0; i < CHIPS[id].count; i++) out.push(id);
  }
  return out;
}

export const BOX_SIZE = 62;

/**
 * A miscount here silently rewrites every probability in the game, and the
 * symptom — "Excalibur seems to show up too often" — is unfalsifiable by eye.
 * The published secondary transcriptions of this rulebook get Merchant and
 * Peasant wrong and sum to 49, which is exactly the failure this catches.
 */
const total = ALL_CHIP_IDS.reduce((n, id) => n + CHIPS[id].count, 0);
if (total !== BOX_SIZE) {
  throw new Error(`[excalibur] chip catalogue sums to ${total}, expected ${BOX_SIZE}`);
}
